import React from 'react'
import { useTranslation } from 'react-i18next'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { Button } from '@components/Button/Button'
import Footer from '@components/Footer'
import Header from '@components/Header'
import Page from '@components/Page'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { TEST_IDS } from 'src/constants/testIds'
import { Body } from './Body'
import { completedInsurance, completedPatientType } from 'src/services/analytics'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { useNearestAvailabilities } from '@hooks/useNearestAvailabilities'
import { SelectedSlotInfo } from '@screens/Patient/SelectedSlotInfo'
import { usePhoneInput } from '@screens/OldPatientIdentification/OldPatientPhoneInput/usePhoneInput'

interface Props {
  onBackClicked: () => void
}

const PatientScreen = ({ onBackClicked }: Props) => {
  const { patientType, selectedBookableDateAndTime } = useChoiceState()
  const { isInternalClinic } = useConfig()
  const { t } = useTranslation()
  const history = useHistory()
  const { data: nearestAvailabilities, isLoading } = useNearestAvailabilities()
  const noAvailableSlots = nearestAvailabilities?.length === 0 && !isLoading
  const isNewPatient = patientType === PatientTypeOptions.NEW
  const userSelectedSlot = !!selectedBookableDateAndTime.originalStartTimestamp
  const isCtaVisible = patientType && ((!isNewPatient && isInternalClinic) || userSelectedSlot || noAvailableSlots)

  const handleNewPatientNextClicked = () => {
    completedPatientType()
    completedInsurance()
    history.push(routes.contact)
  }
  const handleMoreClicked = () => {
    completedPatientType()
    completedInsurance()
    history.push(routes.appointment)
  }

  const phoneInput = usePhoneInput()

  const handleNextClicked = () => {
    if (isNewPatient || !isInternalClinic) {
      noAvailableSlots ? handleMoreClicked() : handleNewPatientNextClicked()
    } else {
      phoneInput.handleSubmit(phoneInput.onSubmit)()
    }
  }

  const disabled = (() => {
    if (!patientType) return true
    if (isNewPatient) {
      if (userSelectedSlot) return false
      if (noAvailableSlots) return false
      return true
    } else {
      if (!!phoneInput.errors.phone) return true
      return false
    }
  })()

  return (
    <Page
      header={<Header onBackClick={onBackClicked} desktopHeadingText={t('patient_page_desktop_headline')} />}
      body={<Body phoneInput={phoneInput} afterTimeslotSelect={handleNewPatientNextClicked} />}
      footer={
        isCtaVisible ? (
          <Footer
            beforeButton={userSelectedSlot ? <SelectedSlotInfo /> : undefined}
            button={
              <Button
                fullSize
                type="button"
                onClick={handleNextClicked}
                disabled={disabled}
                data-test={TEST_IDS.nextButton}
                text={t('next')}
              />
            }
          />
        ) : undefined
      }
    />
  )
}

export default PatientScreen
